import { useMemo, useState } from "react";
import { useCrm, ssdOptions } from "@/lib/crm";
import { MESI, GIORNI, dateIt } from "@/lib/constants";
import { AttivitaDialog } from "@/components/AttivitaDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, CheckCircle2, Circle, Trash2, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useInvitiSync } from "@/lib/inviti";

const today = new Date();

export default function Programmazione() {
  const { attivita, portfolio, ssd, api, refresh } = useCrm();
  const [cursor, setCursor] = useState({ y: 2026, m: 9 }); // Ottobre 2026 (mese 0-based)
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [presetDate, setPresetDate] = useState("");
  const { isTitolare } = useAuth();
  const { sync, syncing } = useInvitiSync(api, refresh, true);

  const options = useMemo(() => ssdOptions(portfolio, ssd), [portfolio, ssd]);

  const ym = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
  const monthActs = useMemo(
    () => attivita.filter((a) => (a.data || "").slice(0, 7) === ym),
    [attivita, ym]
  );

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const offset = (first.getDay() + 6) % 7; // Mon-first
    const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= days; d++) {
      const ds = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      arr.push({ d, ds, acts: monthActs.filter((a) => a.data === ds) });
    }
    return arr;
  }, [cursor, monthActs]);

  const move = (delta) => {
    let m = cursor.m + delta, y = cursor.y;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCursor({ y, m });
  };

  const save = async (data, opts = {}) => {
    let saved;
    if (editing) {
      saved = await api.updateAttivita(editing.id, data);
      toast.success("Attività aggiornata");
    } else {
      saved = await api.createAttivita(data);
      toast.success("Attività aggiunta");
    }
    if (opts.invite) {
      try {
        const r = await api.inviaInvito(saved.id);
        toast.success(`Invito inviato a ${r.inviati} partecipanti`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Invio invito non riuscito");
      }
    }
    setOpen(false);
    setEditing(null);
    setPresetDate("");
    await refresh();
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'evento "${a.titolo || a.tipo}" del ${dateIt(a.data)}?`)) return;
    await api.deleteAttivita(a.id);
    toast.success("Evento eliminato");
    setOpen(false);
    setEditing(null);
    await refresh();
  };

  const upcoming = useMemo(
    () => [...attivita].filter((a) => a.stato !== "fatto").sort((a, b) => (a.data || "").localeCompare(b.data || "")).slice(0, 6),
    [attivita]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Programmazione</h1>
          <p className="text-slate-500 mt-1">Calendario delle attività di tutte le società (portfolio e pipeline).</p>
        </div>
        <div className="flex gap-2">
          {(
            <Button data-testid="sync-risposte-button" variant="outline" disabled={syncing} onClick={() => sync(true)} className="rounded-full gap-1.5">
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Aggiorna conferme
            </Button>
          )}
          <Button data-testid="add-programmazione-button" onClick={() => { setEditing(null); setPresetDate(""); setOpen(true); }} className="rounded-full gap-1.5">
            <Plus className="h-4 w-4" /> Nuova attività
          </Button>
        </div>
      </div>

      <Card className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><ChevronLeft className="h-5 w-5" /></button>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white" data-testid="cal-title">{MESI[cursor.m]} {cursor.y}</h2>
          <button data-testid="cal-next" onClick={() => move(1)} className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><ChevronRight className="h-5 w-5" /></button>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {GIORNI.map((g) => <div key={g} className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 py-1">{g}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((c, i) => (
            <div key={i} data-testid={c ? `cal-day-${c.ds}` : `cal-empty-${i}`}
              onClick={() => c && (setEditing(null), setPresetDate(c.ds), setOpen(true))}
              className={`min-h-[92px] rounded-lg border p-1.5 text-left transition-colors ${c ? "border-slate-200 dark:border-slate-800 hover:border-primary/40 cursor-pointer bg-white dark:bg-slate-900" : "border-transparent"}`}>
              {c && (
                <>
                  <div className={`text-xs font-semibold mb-1 ${c.ds === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}` ? "text-primary" : "text-slate-500"}`}>{c.d}</div>
                  <div className="space-y-1">
                    {c.acts.slice(0, 3).map((a) => (
                      <div key={a.id} onClick={(e) => { e.stopPropagation(); setEditing(a); setOpen(true); }}
                        className={`truncate text-[10px] px-1.5 py-0.5 rounded ${a.stato === "fatto" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 line-through" : "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"}`}
                        title={`${a.titolo} · ${a.ssd_nome}`}>
                        {a.ora ? <span className="font-semibold">{a.ora} </span> : ""}{a.titolo}
                      </div>
                    ))}
                    {c.acts.length > 3 && <div className="text-[10px] text-slate-400 px-1.5">+{c.acts.length - 3} altre</div>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Prossime attività</h2>
        </div>
        <div className="space-y-2">
          {upcoming.length === 0 && <p className="text-sm text-slate-400">Nessuna attività in programma.</p>}
          {upcoming.map((a) => (
            <div key={a.id} data-testid={`upcoming-${a.id}`} onClick={() => { setEditing(a); setOpen(true); }}
              className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3 cursor-pointer hover:border-primary/40 transition-colors">
              {a.stato === "fatto" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{a.titolo}</div>
                <div className="text-xs text-slate-400 truncate">{a.ssd_nome} · {a.tipo}</div>
              </div>
              <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{dateIt(a.data)}{a.ora ? ` · ${a.ora}` : ""}</span>
              <button data-testid={`upcoming-delete-${a.id}`} title="Elimina evento" onClick={(e) => { e.stopPropagation(); remove(a); }} className="text-slate-300 hover:text-destructive transition-colors p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </Card>

      <AttivitaDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setPresetDate(""); } }}
        onSave={save}
        onDelete={remove}
        canInvite
        initial={editing}
        options={options}
        defaultDate={presetDate}
      />
    </div>
  );
}
